import { IComponent, IGraphQLResponse, IOption, IAnswer, ISubject } from '../interfaces';
import { atom, getDefaultStore } from 'jotai/vanilla';
import {
  get_states,
  get_subjects,
  phone_mask_by_country,
  zipcode_mask_by_country,
} from './helpers';
import api from './api';
import application from './application';
import atoms from './index';
import { is_valid_email } from '../components/contact-info/helpers';

export enum ActionTypes {
  subjects = 'subjects',
  states = 'states',
  country = 'country',
  first_name = 'first_name',
  last_name = 'last_name',
  zip_code = 'zip_code',
  preferred_subject = 'preferred_subject',
  phone_number = 'phone_number',
  email = 'email',
}

const MAX_SUBJECT_COUNT = 10;

// Atoms related to custom actions components can perform
interface IActionData {
  options?: IOption[];
  mask?: string;
}

const actions = {
  ['']: {
    data: atom({}),
    validate: atom({}),
    on_set: atom({}),
    parse: atom({}),
  },
  [ActionTypes.subjects]: {
    data: atom<IActionData>({ options: [] }),
    validate: atom(null, (get, set, id: string) => {
      const answers = get<IAnswer[]>(atoms.answers.current_answers);
      const subjects = answers.find((answer) => answer.componentID === id)?.options;
      if (!subjects) return;

      if (subjects.length >= MAX_SUBJECT_COUNT) {
        set(atoms.component.set_props, id, {
          warn: 'You reached the 10-subject limit. To add a new subject, remove one from your list.',
          maxReached: true,
        });
        return;
      }
      set(atoms.component.set_props, id, {
        warn: '',
        maxReached: false,
      });
      return;
    }),
    on_set: atom({}),
    parse: atom({}),
  },
  [ActionTypes.states]: {
    data: atom<IActionData>({ options: [] }),
    validate: atom({}),
    on_set: atom({}),
    parse: atom({}),
  },
  [ActionTypes.country]: {
    data: atom<IActionData>({}),
    validate: atom({}),
    on_set: atom(null, (get, set, updated_state: IComponent): IComponent => {
      const country = (updated_state.value as IOption).id;
      if (updated_state.value) set(application.country, parseInt(country));
      return updated_state;
    }),
    parse: atom({}),
  },
  [ActionTypes.first_name]: {
    data: atom<IActionData>({}),
    validate: atom(null, (get, set, id: string) => {
      const props = get<IComponent[]>(atoms.component.list).find(
        (component) => component.id === id,
      );
      if (!props) return '';
      const value = props.value as string;

      if (value === '') {
        set(atoms.component.set_props, id, { error: 'Please enter your first name.' });
        return;
      }
      set(atoms.component.set_props, id, {
        error: '',
      });
    }),
    on_set: atom({}),
    parse: atom({}),
  },
  [ActionTypes.last_name]: {
    data: atom<IActionData>({}),
    validate: atom(null, (get, set, id: string) => {
      const props = get<IComponent[]>(atoms.component.list).find(
        (component) => component.id === id,
      );
      if (!props) return '';
      const value = props.value as string;
      if (value === '') {
        set(atoms.component.set_props, id, { error: 'Please enter your last name.' });
        return;
      }
      set(atoms.component.set_props, id, {
        error: '',
      });
    }),
    on_set: atom({}),
    parse: atom({}),
  },
  [ActionTypes.zip_code]: {
    data: atom<IActionData>((get) => {
      const country = get(application.country);
      return {
        mask: zipcode_mask_by_country(country),
      };
    }),
    validate: atom(null, (get, set, id: string) => {
      const props = get<IComponent[]>(atoms.component.list).find(
        (component) => component.id === id,
      );
      if (!props) return '';
      const masked_value = props.value as string;
      const mask = props.mask as string;
      if (mask && masked_value) {
        const value = masked_value.replace(/\D/g, '');
        const parsed_mask = mask.replace(/\D/g, '');
        if (value.length !== parsed_mask.length) {
          set(atoms.component.set_props, id, { error: 'Please enter a valid zip code.' });
          return;
        }
      }
      set(atoms.component.set_props, id, {
        error: '',
      });
    }),
    on_set: atom({}),
    parse: atom({}),
  },
  [ActionTypes.phone_number]: {
    data: atom<IActionData>((get) => {
      const country = get(application.country);
      return {
        mask: phone_mask_by_country(country),
      };
    }),
    validate: atom(null, (get, set, id: string) => {
      const props = get<IComponent[]>(atoms.component.list).find(
        (component) => component.id === id,
      );
      if (!props) return '';
      const masked_value = props.value as string;
      const mask = props.mask as string;
      if (mask && masked_value) {
        const value = masked_value.replace(/\D/g, '');
        const parsed_mask = mask.replace(/\D/g, '');
        if (value.length !== parsed_mask.length) {
          set(atoms.component.set_props, id, { error: 'Please enter a valid phone number.' });
          return;
        }
      }
      set(atoms.component.set_props, id, {
        error: '',
      });
    }),
    on_set: atom({}),
    parse: atom(null, (get, set, value: string): string => {
      if (!value) return value;
      return `+${value.replace(/\D/g, '')}`;
    }),
  },
  [ActionTypes.email]: {
    data: atom<IActionData>({}),
    validate: atom(null, (get, set, id: string) => {
      const props = get<IComponent[]>(atoms.component.list).find(
        (component) => component.id === id,
      );
      if (!props) return '';
      const value = props.value as string;

      if (value === '' || !is_valid_email(value)) {
        set(atoms.component.set_props, id, { error: 'Please enter a valid email address' });
        return;
      }
      set(atoms.component.set_props, id, {
        error: '',
      });
    }),
    on_set: atom({}),
    parse: atom({}),
  },
  [ActionTypes.preferred_subject]: {
    data: atom<IActionData>({ options: [] }),
    validate: atom(null, (get, set, id: string) => {
      const components = get<IComponent[]>(atoms.component.list);
      const currentAnswers = get<IAnswer[]>(atoms.answers.current_answers);

      const preferredSubjectComponent = components.find((comp) => comp.id === id);
      if (!preferredSubjectComponent) return;

      const preferredSubjectAnswer = currentAnswers.find((answer) => answer.componentID === id);

      const availableOptions = preferredSubjectComponent.options || [];

      // Auto-select if only one option and no current answer
      if (availableOptions.length === 1 && !preferredSubjectAnswer?.answer) {
        set(atoms.answers.add, {
          componentID: id,
          answer: availableOptions[0].id.toString(),
        });
        return;
      }

      // Clear invalid preferred subject if current answer is not in available options
      if (preferredSubjectAnswer?.answer && availableOptions.length > 0) {
        const currentAnswerId = preferredSubjectAnswer.answer.toString();
        const isStillValid = availableOptions.some(
          (option) => option.id.toString() === currentAnswerId,
        );

        if (!isStillValid) {
          set(atoms.answers.add, {
            componentID: id,
            answer: '',
          });
        }
      }

      // Clear preferred subject if no options available
      if (availableOptions.length === 0 && preferredSubjectAnswer?.answer) {
        set(atoms.answers.add, {
          componentID: id,
          answer: '',
        });
      }
    }),
    on_set: atom({}),
    parse: atom(null, (get, set, value: unknown): string => {
      // Ensure preferred_subject answer is always parsed as string ID
      if (!value) return '';

      // If value is already a string, return it
      if (typeof value === 'string') return value;

      // If value is an object with id, extract the id as string
      if (typeof value === 'object' && value && 'id' in value) {
        return (value as IOption).id.toString();
      }

      // Convert any other type to string
      return String(value);
    }),
  },
};

const setSubjectAtom = (set, subjects: ISubject[], subject: string) => {
  const subjectsSet = [...new Set(subjects)];
  const options = subjectsSet.map((item) => ({ ...item, parentName: item.parent?.name }));
  const value = options.filter(({ shortName }) => subject === shortName);

  set({
    value,
    options,
  });
};

// Function to update preferred_subject options based on selected subjects
export const updatePreferredSubjectOptions = (selectedSubjects: IOption[]) => {
  const store = getDefaultStore();

  // ONLY update options - no answer manipulation to avoid loops
  store.set(actions[ActionTypes.preferred_subject].data, {
    options: selectedSubjects || [],
  });
};

actions[ActionTypes.subjects].data.onMount = (set) => {
  const store = getDefaultStore();
  const { subject } = store.get(application.utm_params);
  const topSubjectsFeatureFlag = store.get(atoms.topSubjectsFeatureFlag);

  if (topSubjectsFeatureFlag) {
    const assetHost =
      process.env.VITE_MODE === 'development'
        ? ''
        : `${process.env.VITE_ASSET_HOST}/${process.env.VITE_BUCKET_FOLDER}`;

    fetch(assetHost + '/subjects.json')
      .then((res) => res.json())
      .then((subjects) => {
        setSubjectAtom(set, subjects, subject);
      });
  } else {
    const client = store.get(api.client);
    const query = get_subjects();

    client.request<IGraphQLResponse>(query).then(({ getAllSubjects: subjects }) => {
      if (!subjects) return;
      setSubjectAtom(set, subjects, subject);
    });
  }
};

actions[ActionTypes.states].data.onMount = (set) => {
  const store = getDefaultStore();
  const client = store.get(api.client);
  const query = get_states();
  client.request<IGraphQLResponse>(query).then(({ getAllStates: options }) => {
    if (options)
      set({
        options: [...new Set(options)],
      });
  });
};

// Initialize preferred subject when the component is mounted
actions[ActionTypes.preferred_subject].data.onMount = () => {
  const store = getDefaultStore();

  // Find selected subjects and initialize preferred subject options
  const currentAnswers = store.get(atoms.answers.current_answers) as IAnswer[];
  const components = store.get(atoms.component.list) as IComponent[];

  const subjectsComponent = components.find((comp) => comp.action === ActionTypes.subjects);
  if (subjectsComponent) {
    const subjectsAnswer = currentAnswers.find(
      (answer) => answer.componentID === subjectsComponent.id,
    );
    const selectedSubjects = subjectsAnswer?.options || [];

    if (selectedSubjects.length > 0) {
      updatePreferredSubjectOptions(selectedSubjects);
    }
  }
};

export default actions;
